"use client";

import { useEffect, useRef, type KeyboardEvent, type RefObject } from "react";
import { createPortal } from "react-dom";
import { HUB_SPORT_GROUPS, hubSportsInGroup } from "@/lib/conditions/hub/sports";
import type { HubSport, HubSportKey } from "@/lib/conditions/hub/types";
import { HubCloseIcon, HubIcon, HubTickIcon, type HubGlyph } from "./hub-icons";
import motion from "./hub-motion.module.css";

/**
 * The phone sport picker (F4): a bottom sheet over a scrim, "All sports" first, then the five
 * groups in two-column grids. Focus moves into the sheet and is trapped there; Escape, the scrim
 * and the close button close it and return focus to the control that opened it; choosing a sport closes it too.
 * Body scroll is locked while it is open. The root sits above the fixed site navbar (z-1000) and
 * below the consent banner (z-1200), so the scrim covers the navbar and the dialog stays modal.
 */

export const HUB_SPORT_SHEET_ID = "hub-sport-sheet";

const FOCUSABLE = 'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

export type HubSportSheetProps = Readonly<{
  open: boolean;
  sports: readonly HubSport[];
  /** The selected sport, or null for All. */
  current: HubSportKey | null;
  /** Snow sports with nothing in season (tagged "Off-season"). */
  offSeason: readonly HubSportKey[];
  /** Board meta per sport, for each option's accessible name ("267 spots ranked"). */
  metaFor: Readonly<Partial<Record<HubSportKey, string>>>;
  /** Accessible name of the All option. */
  allAria: string;
  onSelect: (sport: HubSportKey | null) => void;
  onClose: () => void;
  /** Where focus returns when the sheet closes (the control that opened it). */
  returnFocusRef: RefObject<HTMLElement | null>;
}>;

function SheetOption({
  id,
  label,
  icon,
  on,
  beta,
  off,
  aria,
  onPick,
}: Readonly<{
  id: string;
  label: string;
  icon: HubGlyph;
  on: boolean;
  beta: boolean;
  off: boolean;
  aria: string;
  onPick: () => void;
}>) {
  return (
    <button
      type="button"
      id={id}
      aria-pressed={on}
      aria-label={aria}
      onClick={onPick}
      data-hub-sheet-option={id}
      className={`flex min-h-12 w-full cursor-pointer items-center gap-[9px] rounded-[12px] border py-[5px] pl-[6px] pr-[10px] text-left font-[inherit] transition-colors hover:border-navy ${
        on ? "border-navy bg-navy text-white" : "border-border bg-white text-navy"
      }`}
    >
      <span
        aria-hidden="true"
        className={`flex size-[34px] shrink-0 items-center justify-center rounded-[9px] ${
          on ? "bg-[rgba(91,192,206,0.16)] text-teal-light" : "bg-[#E4F2F3] text-teal-dark"
        }`}
      >
        <HubIcon name={icon} size={20} />
      </span>
      <span className="flex min-w-0 flex-auto flex-col items-start gap-0.5">
        <span className="text-[14px] font-bold leading-[1.15]">{label}</span>
        {beta || off ? (
          <span className="flex items-center gap-[5px]">
            {off ? (
              <span className={`text-[11.5px] font-medium ${on ? "text-[#C9D3E0]" : "text-text-muted"}`}>Off-season</span>
            ) : null}
            {beta ? (
              <span
                className={`rounded-full border px-[5px] text-[9.5px] font-bold uppercase leading-[15px] tracking-[0.04em] ${
                  on ? "border-white/40 text-white" : "border-[#C9D3D6] text-[#566072]"
                }`}
              >
                Beta
              </span>
            ) : null}
          </span>
        ) : null}
      </span>
      {on ? (
        <span aria-hidden="true" className="flex shrink-0 text-teal-light">
          <HubTickIcon />
        </span>
      ) : null}
    </button>
  );
}

export function HubSportSheet({
  open,
  sports,
  current,
  offSeason,
  metaFor,
  allAria,
  onSelect,
  onClose,
  returnFocusRef,
}: HubSportSheetProps) {
  const sheetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const returnTo = returnFocusRef.current;
    const body = document.body;
    const previousOverflow = body.style.overflow;
    body.style.overflow = "hidden";
    const sheet = sheetRef.current;
    const selected = sheet?.querySelector<HTMLElement>('[aria-pressed="true"]');
    (selected ?? sheet?.querySelector<HTMLElement>(FOCUSABLE))?.focus({ preventScroll: true });
    return () => {
      body.style.overflow = previousOverflow;
      returnTo?.focus({ preventScroll: true });
    };
  }, [open, returnFocusRef]);

  if (!open || typeof document === "undefined") return null;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const focusables = Array.from(sheetRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !sheetRef.current?.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !sheetRef.current?.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  };

  const off = new Set(offSeason);
  const groups = HUB_SPORT_GROUPS.map((group) => ({ group, items: hubSportsInGroup(sports, group.key) }))
    .filter((entry) => entry.items.length > 0);

  return createPortal(
    <div className="fixed inset-0 z-[1050] flex flex-col justify-end" data-hub-sheet-root="">
      <div aria-hidden="true" onClick={onClose} className={`absolute inset-0 cursor-pointer bg-[rgba(12,20,36,0.58)] ${motion.scrim}`} />
      <div
        ref={sheetRef}
        id={HUB_SPORT_SHEET_ID}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${HUB_SPORT_SHEET_ID}-heading`}
        onKeyDown={onKeyDown}
        className={`relative max-h-[calc(100dvh-28px)] w-full overflow-y-auto overscroll-contain rounded-t-[22px] bg-white px-4 pb-[18px] pt-2 shadow-[0_-18px_44px_rgba(12,20,36,0.3)] ${motion.sheet}`}
      >
        <span aria-hidden="true" className="mx-auto mb-1.5 block h-[5px] w-10 rounded-full bg-[#D5DBD2]" />
        <div className="flex items-center justify-between gap-2.5">
          <h2 id={`${HUB_SPORT_SHEET_ID}-heading`} className="m-0 text-[19px] font-bold text-navy">
            Choose a sport
          </h2>
          <button
            type="button"
            aria-label="Close sport picker"
            onClick={onClose}
            className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-[12px] border border-border bg-white text-navy"
          >
            <HubCloseIcon size={14} />
          </button>
        </div>
        <div role="group" aria-label="All sports" className="mt-2.5 grid grid-cols-1 gap-1.5">
          <SheetOption
            id={`${HUB_SPORT_SHEET_ID}-all`}
            label="All sports"
            icon="all"
            on={current === null}
            beta={false}
            off={false}
            aria={allAria}
            onPick={() => onSelect(null)}
          />
        </div>
        {groups.map(({ group, items }) => (
          <div key={group.key} role="group" aria-label={`${group.label} sports`} className="mt-2">
            <h3 className="m-0 mb-[3px] pl-0.5 text-[11.5px] font-bold uppercase leading-[18px] tracking-[0.1em] text-[#566072]">
              {group.label}
            </h3>
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-1.5">
              {items.map((sport) => {
                const isOff = off.has(sport.key);
                const meta = metaFor[sport.key];
                const aria = [
                  `${sport.label}, ${group.label} group`,
                  isOff ? "off-season" : null,
                  meta ?? null,
                  sport.beta ? "beta" : null,
                ].filter(Boolean).join(", ");
                return (
                  <SheetOption
                    key={sport.key}
                    id={`${HUB_SPORT_SHEET_ID}-${sport.key}`}
                    label={sport.label}
                    icon={sport.icon}
                    on={current === sport.key}
                    beta={sport.beta}
                    off={isOff}
                    aria={aria}
                    onPick={() => onSelect(sport.key)}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>,
    document.body,
  );
}
